// src/components/home/PostComments.tsx - WITH CLICKABLE USERNAMES
import React from 'react';
import { Link } from 'react-router-dom';
import { Comment, UserData } from './types';
 
interface PostCommentsProps {
  darkMode: boolean;
  postId: string;
  comments: Comment[];
  commentText: string;
  loading: boolean;
  userData: UserData;
  onCommentChange: (text: string) => void;
  onSubmitComment: () => void;
  formatTime: (isoString: string) => string;
}
 
export const PostComments: React.FC<PostCommentsProps> = ({
  darkMode, comments, commentText, loading,
  userData, onCommentChange, onSubmitComment, formatTime,
}) => {
  const borderColor = darkMode ? 'rgba(90,184,184,0.1)' : 'rgba(13,26,26,0.08)';
  const inputBg = darkMode ? '#0d1a1a' : '#ffffff';
  const inputBorder = darkMode ? 'rgba(90,184,184,0.2)' : 'rgba(13,26,26,0.15)';
 
  return (
    <div
      className="border-t px-5 py-4"
      style={{
        borderColor,
        background: darkMode ? 'rgba(13,26,26,0.5)' : 'rgba(224,244,244,0.4)',
      }}
    >
      {/* Comment input */}
      <div className="flex items-start gap-3 mb-4">
        <img src={userData.photoURL} alt={userData.username} className="w-8 h-8 rounded-full flex-shrink-0" />
        <div className="flex-1">
          <textarea
            value={commentText}
            onChange={e => onCommentChange(e.target.value)}
            placeholder="Add a comment..."
            rows={2}
            className="w-full p-3 rounded-lg resize-none text-sm focus:outline-none transition-all font-medium"
            style={{
              background: inputBg,
              border: `1.5px solid ${inputBorder}`,
              color: darkMode ? '#f0fafa' : '#0d1a1a',
            }}
            onFocus={e => (e.target.style.borderColor = '#5ab8b8')}
            onBlur={e => (e.target.style.borderColor = inputBorder)}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={onSubmitComment}
              disabled={!commentText.trim()}
              className="px-4 py-2 rounded-lg text-xs font-black transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: '#e8a820',
                border: '2px solid #0d1a1a',
                boxShadow: '2px 2px 0 #0d1a1a',
                color: '#0d1a1a',
                letterSpacing: '1px',
              }}
              onMouseEnter={e => { if (commentText.trim()) { (e.currentTarget as HTMLButtonElement).style.transform = 'translate(1px,1px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '1px 1px 0 #0d1a1a'; } }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; (e.currentTarget as HTMLButtonElement).style.boxShadow = '2px 2px 0 #0d1a1a'; }}
            >
              COMMENT
            </button>
          </div>
        </div>
      </div>
 
      {/* Comment list */}
      {loading ? (
        <div className="flex justify-center py-4">
          <div
            className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: '#5ab8b8', borderTopColor: 'transparent' }}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map(comment => (
            <div key={comment.id} className="flex items-start gap-3">
              <Link to={comment.author?.username ? `/profile/${comment.author.username}` : '#'}>
                <img
                  src={comment.author?.avatar_url || 'https://i.pravatar.cc/150'}
                  alt={comment.author?.username || 'User'}
                  className="w-7 h-7 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                />
              </Link>
              <div className="flex-1">
                <div
                  className="rounded-xl p-3"
                  style={{ background: darkMode ? '#0d1a1a' : '#ffffff', border: `1px solid ${borderColor}` }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Link
                      to={comment.author?.username ? `/profile/${comment.author.username}` : '#'}
                      className="text-xs font-black hover:underline"
                      style={{ color: darkMode ? '#f0fafa' : '#0d1a1a' }}
                    >
                      {comment.author?.username || 'Anonymous'}
                    </Link>
                    <span className="text-[10px] font-bold" style={{ color: darkMode ? 'rgba(126,206,206,0.35)' : 'rgba(13,26,26,0.4)' }}>
                      {formatTime(comment.created_at)}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: darkMode ? 'rgba(240,250,250,0.8)' : '#0d1a1a' }}>
                    {comment.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
 
          {comments.length === 0 && (
            <p
              className="text-center text-xs py-4 font-bold"
              style={{ color: darkMode ? 'rgba(126,206,206,0.3)' : 'rgba(13,26,26,0.35)', letterSpacing: '1px' }}
            >
              NO COMMENTS YET. BE THE FIRST.
            </p>
          )}
        </div>
      )}
    </div>
  );
};